import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  AimOutlined,
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  MoreOutlined,
  NotificationOutlined,
  SoundOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { App, Button, Dropdown, ModalFuncProps, Tooltip } from 'antd';
import { ArgsProps } from 'antd/es/notification';

import { SmsServiceType } from '../../../../shared/constants/enums';
import { InitializeUpdateForRelatedUsers } from '../../../../shared/models';
import * as sharedActions from '../../../../shared/store/actions';
import { AppDispatch, RootState } from '../../../../store';
import ChangeChatNameModal from '../../modals/change-chat-name-modal';
import LinkToInsuredModal from '../../modals/link-to-insured-modal';
import { ChatsCollection, SmsChat } from '../../models';
import * as actions from '../../store/actions';

import { isNumber } from 'lodash';

import './styles.less';

type ChatMessagesHeaderActionsProps = {
  disabled: boolean;
  updateMessageControl: () => void;
};

const ChatMessagesHeaderActions = ({ disabled, updateMessageControl }: ChatMessagesHeaderActionsProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { notification, modal, message } = App.useApp();

  const [showSelectLinkInsureds, setShowSelectLinkInsureds] = useState<{
    visible: boolean;
    smsChatId: string;
  }>({
    visible: false,
    smsChatId: '',
  });

  const [showChangeChatName, setShowChangeChatName] = useState<{
    visible: boolean;
    smsChatId: string;
    currentName: string;
  }>({
    visible: false,
    smsChatId: '',
    currentName: '',
  });

  const [loadingIndicator, setLoadingIndicator] = useState<() => void>();

  const { selectedChatItem, messageControlParameters } = useSelector((state: RootState) => state.smses);
  const {
    unlinkConversationToInsuredsNotesLoading,
    deleteChatLoading,
    markAsUnreadResultLoading,
    markAsStarredLoading,
    muteNotificationResultLoading,
    localChats,
  } = useSelector((state: RootState) => state.smsChat);
  const { smsSetting } = useSelector((state: RootState) => state.smsSetting);
  const { localMessages } = useSelector((state: RootState) => state.smses);

  useEffect(() => {
    if (showChangeChatName.visible || showSelectLinkInsureds.visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showChangeChatName, showSelectLinkInsureds]);

  useEffect(() => {
    if (unlinkConversationToInsuredsNotesLoading || deleteChatLoading || markAsUnreadResultLoading || markAsStarredLoading) {
      let hide = message.loading('Action in progress..', 0);
      setLoadingIndicator(() => hide);
    } else if (loadingIndicator) {
      loadingIndicator();
    }
  }, [unlinkConversationToInsuredsNotesLoading, deleteChatLoading, markAsUnreadResultLoading, markAsStarredLoading]);

  const onMarkAsUnread = (smsChatId: string, unreadCount: number) => {
    if (!localMessages || !localMessages.messageList) {
      return;
    }

    if (!(isNumber(unreadCount) && unreadCount > 0)) {
      dispatch(
        actions.markAsUnread({
          smsChatId,
          smsId: localMessages.messageList[localMessages.messageList.length - 1].id,
        }),
      ).then(() => {
        let newChatList = localChats?.chatList?.slice() ?? [];

        let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
        let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

        chatItem.unreadCount++;

        newChatList[chatListIndex] = chatItem;

        let newChatItems = { ...localChats } as ChatsCollection;
        newChatItems.chatList = newChatList;

        dispatch(actions.setLocalChats(newChatItems));
        dispatch(actions.setSelectedChatItem(undefined));
        updateMessageControl();
      });
    }
  };

  const unlinkConversationToInsuredsNotes = (smsChatId: string) => {
    dispatch(actions.unlinkConversationToInsuredsNotes({ smsChatId })).then(() => {
      notification.success({
        message: 'Success',
        top: 55,
        duration: 10,
        description: `Conversation to Insured's Notes was unlinked`,
      } as ArgsProps);

      let newChatList = localChats?.chatList?.slice() ?? [];

      let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
      let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

      chatItem.truckingCompanyId = undefined;

      newChatList[chatListIndex] = chatItem;

      let newChatItems = { ...localChats } as ChatsCollection;
      newChatItems.chatList = newChatList;

      dispatch(actions.setLocalChats(newChatItems));
      dispatch(actions.setSelectedChatItem(chatItem));
      updateMessageControl();
    });
  };

  const muteNotification = ({ smsChatId, isMute }: { smsChatId: string; isMute?: boolean }) => {
    modal.confirm({
      title: `Are you sure you want to ${!isMute ? 'mute' : 'unmute'} next notification for this chat?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      confirmLoading: muteNotificationResultLoading,
      okButtonProps: { disabled: muteNotificationResultLoading },
      async onOk() {
        await dispatch(
          actions.muteNotification({
            smsChatId,
            isMute: !isMute,
          }),
        );

        notification.success({
          message: 'Success',
          top: 55,
          duration: 10,
          description: `Chat was ${!isMute ? 'muted' : 'unmuted'}`,
        } as ArgsProps);

        let newChatList = localChats?.chatList?.slice() ?? [];
        let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
        let chatItem = { ...newChatList[chatListIndex] } as SmsChat;
        chatItem.isMute = !isMute;
        newChatList[chatListIndex] = chatItem;
        let newChatItems = { ...localChats } as ChatsCollection;
        newChatItems.chatList = newChatList;
        dispatch(actions.setLocalChats(newChatItems));
        dispatch(actions.setSelectedChatItem(chatItem));
        updateMessageControl();
      },
    } as ModalFuncProps);
  };

  const markAsStarred = async ({ smsChatId, starred }: { smsChatId: string; starred: boolean }) => {
    await dispatch(
      actions.markAsStarred({
        smsChatId,
        starred: !starred,
      }),
    );

    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      description: `Chat was ${!starred ? 'starred' : 'unstarred'}`,
    } as ArgsProps);

    let newChatList = localChats?.chatList?.slice() ?? [];
    let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);

    let chatItem = { ...newChatList[chatListIndex] } as SmsChat;
    chatItem.starred = !starred;

    newChatList[chatListIndex] = chatItem;

    let newChatItems = { ...localChats } as ChatsCollection;

    newChatItems.chatList = newChatList;

    dispatch(actions.setLocalChats(newChatItems));
    dispatch(actions.setSelectedChatItem(chatItem));
    updateMessageControl();
  };

  const deleteChat = ({ smsChatId }: { smsChatId: string }) => {
    if (!messageControlParameters) {
      return;
    }

    modal.confirm({
      title: 'The removal operation may take several minutes. Are you sure you want to delete this chat?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      confirmLoading: deleteChatLoading,
      okButtonProps: { disabled: deleteChatLoading },
      async onOk() {
        const result = await dispatch(
          actions.deleteChat({
            smsChatId,
          }),
        );

        notification.success({
          message: 'Success',
          top: 55,
          duration: 10,
          description: `Chat was deleted`,
        } as ArgsProps);

        if (result === true) {
          const newChatItems = { ...localChats } as ChatsCollection;
          newChatItems.chatList = newChatItems.chatList?.filter((x) => x.smsChatId !== smsChatId).slice() ?? [];

          dispatch(actions.setLocalChats(newChatItems));
          dispatch(actions.setSelectedChatItem(undefined));
          updateMessageControl();

          dispatch(
            sharedActions.sendMessage({
              isInitializeUpdateForRelatedUsers: true,
              agencyId: messageControlParameters.agencyId,
              isGroupAccount: messageControlParameters.smsServiceType === SmsServiceType.PhoneGroup,
              phoneGroupPhoneNumber: messageControlParameters.myNumber,
              isAgencySmsService:
                messageControlParameters.smsServiceType === SmsServiceType.AgencyOwnRingCentral ||
                messageControlParameters.smsServiceType === SmsServiceType.AgencyTwilio ||
                messageControlParameters.smsServiceType === SmsServiceType.AgencySpoke ||
                messageControlParameters.smsServiceType === SmsServiceType.AgencyGlobalTelecom,
              allowUsageFromAgency: smsSetting?.allowUsageFromAgency,
            } as InitializeUpdateForRelatedUsers),
          );
        }
      },
    } as ModalFuncProps);
  };

  const menu = (
    smsChatId: string,
    currentName: string,
    truckingCompanyId: string | undefined,
    unreadCount: number,
    isMute: boolean | undefined,
    starred: boolean,
  ) => [
    {
      label: (
        <>
          <MailOutlined /> Mark as unread
        </>
      ),
      key: '1',
      onClick: () => onMarkAsUnread(smsChatId, unreadCount),
    },
    !truckingCompanyId
      ? {
          label: (
            <>
              <ApiOutlined /> Link Conversation to Insured's Notes
            </>
          ),
          key: '2',
          onClick: () => setShowSelectLinkInsureds({ visible: true, smsChatId }),
          title: "Link to Insured and Synchronize SMS messages with the insured's notes",
        }
      : {
          label: (
            <>
              <AimOutlined /> Unlink Conversation from Insured's Notes
            </>
          ),
          key: '2',
          onClick: () => unlinkConversationToInsuredsNotes(smsChatId),
          title: "Unlink from Insured and Resynchronize SMS messages with the insured's notes",
        },
    {
      label: (
        <>
          <EditOutlined /> Change chat name
        </>
      ),
      key: '3',
      onClick: () => setShowChangeChatName({ visible: true, smsChatId, currentName }),
    },
    {
      label: (
        <Tooltip title={smsSetting?.notificationsOnlyForRecipient === false ? "The 'Use notification subscription' is disabled" : ''}>
          {!isMute ? (
            <>
              <NotificationOutlined /> Mute
            </>
          ) : (
            <>
              <SoundOutlined /> Unmute
            </>
          )}
        </Tooltip>
      ),
      key: '4',
      onClick: () => muteNotification({ smsChatId, isMute }),
      disabled: smsSetting?.notificationsOnlyForRecipient === false,
    },
    {
      label: !starred ? (
        <>
          <StarOutlined /> Mark as starred
        </>
      ) : (
        <>
          <StarOutlined /> Unmark as starred
        </>
      ),
      key: '6',
      onClick: () => markAsStarred({ smsChatId, starred }),
    },
    {
      label: (
        <>
          <DeleteOutlined /> Delete chat
        </>
      ),
      key: '5',
      onClick: () => deleteChat({ smsChatId }),
    },
  ];

  return (
    <>
      <Dropdown
        key="more"
        menu={{
          items: selectedChatItem
            ? menu(
                selectedChatItem.smsChatId,
                selectedChatItem.chatName,
                selectedChatItem.truckingCompanyId,
                selectedChatItem.unreadCount,
                selectedChatItem.isMute,
                selectedChatItem.starred,
              )
            : [],
        }}
        placement="bottomRight"
        trigger={['click']}
        disabled={disabled}
      >
        <Button type="text" icon={<MoreOutlined style={{ fontSize: 20 }} />} disabled={disabled} />
      </Dropdown>

      {showSelectLinkInsureds.visible ? (
        <LinkToInsuredModal
          visible={showSelectLinkInsureds.visible}
          onClose={() => setShowSelectLinkInsureds({ visible: false, smsChatId: '' })}
          smsChatId={showSelectLinkInsureds.smsChatId}
          updateMessageControl={updateMessageControl}
        />
      ) : undefined}

      {showChangeChatName.visible && showChangeChatName.smsChatId && showChangeChatName.currentName ? (
        <ChangeChatNameModal
          visible={showChangeChatName.visible}
          onClose={() =>
            setShowChangeChatName({
              visible: false,
              smsChatId: '',
              currentName: '',
            })
          }
          smsChatId={showChangeChatName.smsChatId}
          currentName={showChangeChatName.currentName}
          updateMessageControl={updateMessageControl}
        />
      ) : undefined}
    </>
  );
};

export default ChatMessagesHeaderActions;
